<template>
  <div class="px-4 py-4 max-w-screen-xl m-auto">
    <div
      class="flex flex-col md:flex-row items-center justify-center gap-12 md:gap-24 mb-8"
    >
      <ReviewShowcaseTitle
        :average-rating="averageRating"
        :review-count="getRealReviewsCount"
        :review-type="reviewType"
      />
      <ReviewBreakdown
        v-if="
          reviewBreakdown &&
          getRealReviewsCount > 1 &&
          hasReviewBreakdownData(reviewBreakdown)
        "
        :breakdown="reviewBreakdown"
        @click:stars="onBreakdownStarsClick"
      />
    </div>
    <div class="text-h5 text-center md:text-left mb-4 px-4">
      {{ t('review.showcase.latestReviews') }}
    </div>
    <div class="flex flex-row overflow-hidden justify-center">
      <ErrorBoundary>
        <component
          :is="wrapperComponent"
          data-test-id="review-showcase-container"
          :items-length="amountOfReviewsToRender"
          :class="{
            'flex flex-col gap-4 w-full': isMobile,
            'max-w-[780px] flex justify-center': amountOfReviewsToRender === 1,
          }"
          :hide-navigation="computedReviews.length <= 3"
          @scroll:right="carouselIdx = carouselIdx + 1"
          @scroll:left="carouselIdx--"
        >
          <ReviewCard
            v-for="i in amountOfReviewsToRender"
            :key="i"
            data-test-id="review-card-1"
            v-bind="getReviewCardProps[i - 1]"
            open-reply-in-sidebar
            @click:image="clickOnImage(getReviewCardProps[i - 1].id)"
            @expand:partner-reply="clickOnImage(getReviewCardProps[i - 1].id)"
          >
            <template #title>
              <div class="flex">
                <div
                  class="text-sm md:text-base font-semibold text-black"
                  data-test-id="review-title"
                >
                  {{ getReviewCardProps[i - 1].reviewerName }}
                </div>
              </div>
            </template>

            <template #footer="{ reviewedAt }">
              <div class="text-xs" data-test-id="review-created-at">
                {{ reviewedAt }}
              </div>
            </template>
          </ReviewCard>
        </component>
      </ErrorBoundary>
    </div>

    <div v-if="computedReviews.length > 6" class="justify-center flex mt-4">
      <Button
        variant="outlined"
        class="max-w-[368px] w-full py-3 md:py-2 font-semibold"
        @click="isSidebarOpen = true"
      >
        {{ t('review.seeAll') }}
      </Button>
    </div>

    <ReviewSidebar
      :is-open="isSidebarOpen"
      :reviews="computedReviews"
      :average-rating="averageRating"
      :review-count="reviewCount"
      :selected-review-id="selectedReviewId"
      :review-type="reviewType"
      :event-permalink="eventPermalink"
      :review-breakdown="reviewBreakdown"
      :initial-rating-filter="initialRatingFilter"
      @close="closeSidebar"
      @update:selected-review-id="selectedReviewId = $event"
    />
  </div>
</template>
<script lang="ts" setup>
import dayjs from 'dayjs'
import type { Review, ReviewBreakdown } from '~/composables/types/event'
import { hasReviewBreakdownData } from '~/utils/reviewSidebarHelpers'

const { t, locale } = useI18n()
const { isMobile } = useDevice()

const emit = defineEmits<{
  (e: 'clear-open-review'): void
}>()

const {
  reviews = [],
  reviewCount = 0,
  averageRating = '',
  displayItemTitle = false,
  reviewType = 'event',
  eventPermalink,
  reviewBreakdown,
  openWithReviewId,
} = defineProps<{
  reviews: Review[]
  averageRating: string
  reviewCount: number
  supplierName: string
  supplierCreatedAt: string
  displayItemTitle: boolean
  reviewType?: 'event' | 'partner' | 'third-party'
  eventPermalink?: string
  reviewBreakdown?: Record<string, number>
  reviewBreakdown?: ReviewBreakdown | null
  openWithReviewId?: string
}>()

const isSidebarOpen = ref(false)
const selectedReviewId = ref<string | undefined>(undefined)
const initialRatingFilter = ref<number | null>(null)
const carouselIdx = ref(0)

const onBreakdownStarsClick = (stars: number) => {
  initialRatingFilter.value = stars
  isSidebarOpen.value = true
}

const clickOnImage = (id: string) => {
  selectedReviewId.value = id
  isSidebarOpen.value = true
}

const closeSidebar = () => {
  isSidebarOpen.value = false
  selectedReviewId.value = undefined
  initialRatingFilter.value = null
  emit('clear-open-review')
}

watch(
  () => openWithReviewId,
  (id) => {
    if (id) {
      selectedReviewId.value = id
      isSidebarOpen.value = true
    }
  },
  { immediate: true }
)

const getRealReviewsCount = computed(() => {
  const reviewsLength = reviews.length

  return reviewCount >= reviewsLength ? reviewCount : reviewsLength
})

const computedReviews = computed(
  () =>
    [...reviews]?.sort((a, b) => {
      return dayjs(a.created_at).isBefore(dayjs(b.created_at)) ? 1 : -1
    }) || []
)

const computedReviewsList = computed(() => {
  const length = computedReviews.value.length

  if (isMobile) return [...computedReviews.value].splice(0, 3)

  const adjustedIdx = (idx: number) => (length + (idx % length)) % length

  return [
    computedReviews.value[adjustedIdx(0 + carouselIdx.value)],
    computedReviews.value[adjustedIdx(1 + carouselIdx.value)],
    computedReviews.value[adjustedIdx(2 + carouselIdx.value)],
  ]
})

const amountOfReviewsToRender = computed(() =>
  Math.min(computedReviews.value.length, computedReviewsList.value.length)
)

const getReviewCardProps = computed(() =>
  computedReviewsList.value.map((review, i) => ({
    id: review.id,
    averageRating: review.rating,
    reviewerName: review.customer,
    isTeamevent: false,
    verified: review.verified,
    reviewedAt: dateToFormat(review.created_at, 'MMMM D, YYYY', locale.value),
    reviewContent: review.review,
    images: review.media?.data,
    supplierReply: review?.supplier_reply,
    class: {
      'min-[1000px]:w-full  min-[1000px]:block': true,
      hidden: i > 0 && !isMobile,
      'md:min-h-[295px]': review.media?.data.length > 0,
      'md:min-h-[210px]': !review.media || review.media?.data.length === 0,
    },
    ...(review.item && displayItemTitle ? { itemTitle: review.item } : {}),
  }))
)

const wrapperComponent = computed(() =>
  isMobile ? 'div' : resolveComponent('HorizontalCarouselWrapper')
)
</script>

<style scoped>
svg {
  display: inline;
}
</style>
